import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {SubscriptionPlan} from '../../../entity/enums/subscription-plan';
import {useService} from '../../../infrastructure/service/service';
import {SubscriptionDto} from '../../../entity/dto/subscription-dto';
import {SubscriptionsResponseObject} from '../../../entity/resource/subscriptions-response';
import {SubscriptionStatus} from '../../../entity/enums/subscription-status';
import {User} from '../../../entity/model/user';
import Box from '@mui/material/Box';
import {
  Button,
  CircularProgress, FormControlLabel,
  Paper, Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LoadingComponent from '../components/widget/LoadingComponent';
import {SubscriptionResource} from '../../../entity/resource/subscription-resource';
import SubscriptionDialog from '../components/subsciptions/SubscriptionDialog';
import CloseIcon from '@mui/icons-material/Close';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/subscription.css';

const Subscription = (): JSX.Element => {
  const {subscriptionService, userService} = useService();

  const [currentUser, setCurrentUser] = useState<User>();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<SubscriptionsResponseObject[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<boolean>(false);
  const [trialStatus, setTrialStatus] = useState<boolean>(false);
  const [cancelStatus, setCancelStatus] = useState<boolean>(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan>(SubscriptionPlan.TRIAL);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(true);
  const [subscriptionResources, setSubscriptionResources] = useState<SubscriptionResource[]>([]);
  const [annualSwitch, setAnnualSwitch] = useState<boolean>(false);

  const tableHeaders = [
    '',
    SubscriptionPlan.TRIAL.toString(),
    SubscriptionPlan.SOLO.toString(),
    SubscriptionPlan.ENTERPRISE.toString()
  ];

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const subscriptions = await subscriptionService.getSubscriptions(user?.organizationID ?? '');
      setSubscriptions(subscriptions);

      setSubscriptionStatus(subscriptions.length > 0 && subscriptions.filter((subscriptionsResponseObject: SubscriptionsResponseObject) => subscriptionsResponseObject.status === SubscriptionStatus.ACTIVE).length > 0);
      setTrialStatus(subscriptions.length > 0 && subscriptions.filter((subscriptionsResponseObject: SubscriptionsResponseObject) => subscriptionsResponseObject.status === SubscriptionStatus.TRAIL).length > 0);
      setCancelStatus(subscriptions.length > 0 && subscriptions.filter((subscriptionsResponseObject: SubscriptionsResponseObject) => subscriptionsResponseObject.status === SubscriptionStatus.CANCELED).length > 0);

      setSubscriptionResources(subscriptionService.getSubscriptionResources());
    }

    if (reload)
      init().then(() => setLoading(false));
  }, [reload]);

  const handleAnnualSwitchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAnnualSwitch(event.target.checked);
  }

  const handleSubscription = async (subscriptionPlan: SubscriptionPlan): Promise<void> => {
    setSubmitLoading(true);
    setSubscriptionPlan(subscriptionPlan);

    if (subscriptionPlan === SubscriptionPlan.ENTERPRISE)
      window.open('https://zitles.com/#demo', '_blank');
    else {
      if (subscriptionStatus || trialStatus) {
        setOpenSubscriptionDialog(true);
        setReload(false);
      } else {
        const subscriptionDto: SubscriptionDto = {
          noTrial: subscriptionPlan !== SubscriptionPlan.TRIAL,
          trialPeriodDays: 30
        };

        const response = await subscriptionService.subscribe(currentUser?.organizationID ?? '', annualSwitch, subscriptionDto);
        if (response)
          window.location.href = response.url;
      }
    }

    setSubmitLoading(false);
  }

  const renderTableRow = (subscriptionResource: SubscriptionResource, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={index}
        className={'table-cell'}>
        <TableCell id={`text-subscription-feature-${index}`} className={'table-cell-text subscription-feature'} align={'left'}>
          {
            subscriptionResource.feature !== ''
              ? subscriptionResource.feature
              : <Box display='flex' alignItems='center'>
                  <Typography className={'data-table-header'}>Monthly</Typography>
                  <FormControlLabel
                    id={'switch-subscription-annual'}
                    control={ <Switch checked={annualSwitch} onChange={handleAnnualSwitchChange} className={'subscription-switch'}/> }
                    label=''/>
                  <Typography className={'data-table-header'} sx={{marginLeft: '-16px'}}>Annually</Typography>
                </Box>
          }
        </TableCell>
        <TableCell id={`text-subscription-trial-${index}`} className={'table-cell-text'} align={'center'}>
          {
            typeof subscriptionResource.trial === 'string'
              ? subscriptionResource.feature !== ''
                ? subscriptionResource.trial
                : <Box>
                    <Typography className={'subscription-price'}>{subscriptionResource.trial.split('-')[0]}</Typography>
                    <Typography className={'subscription-duration'}>{subscriptionResource.trial.split('-')[1]}</Typography>
                  </Box>
              : subscriptionResource.trial
                ? <CheckIcon color={'success'}/>
                : <CloseIcon color={'error'}/>
          }
        </TableCell>
        <TableCell id={`text-subscription-solo-${index}`} className={'table-cell-text'} align={'center'}>
          {
            typeof subscriptionResource.solo === 'string'
              ? subscriptionResource.feature !== ''
                ? subscriptionResource.solo
                : <Box>
                    <Typography className={'subscription-price'}>
                      {
                        annualSwitch
                          ? '$1000'
                          : subscriptionResource.solo.split('-')[0]
                      }
                    </Typography>
                    <Typography className={'subscription-duration'}>
                      {
                        annualSwitch
                          ? 'user / year'
                          : subscriptionResource.solo.split('-')[1]
                      }
                    </Typography>
                  </Box>
              : subscriptionResource.solo
                ? <CheckIcon color={'success'}/>
                : <CloseIcon color={'error'}/>
          }
        </TableCell>
        <TableCell id={`text-subscription-enterprise-${index}`} className={'table-cell-text'} align={'center'}>
          {
            typeof subscriptionResource.enterprise === 'string'
              ? subscriptionResource.feature !== ''
                ? subscriptionResource.enterprise
                : <Typography className={'subscription-price'}>{subscriptionResource.enterprise}</Typography>
              : subscriptionResource.enterprise
                ? <CheckIcon color={'success'}/>
                : <CloseIcon color={'error'}/>
          }
        </TableCell>
      </TableRow>
    );
  }

  const renderTableButtons = (): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={'button'}
        className={'table-cell'}>
        <TableCell className={'table-cell-text subscription-feature'} align={'center'}/>
        <TableCell className={'table-cell-text'} align={'center'}>
          <Button
            id={'btn-subscription-trial'}
            disabled={(submitLoading && subscriptionPlan === SubscriptionPlan.TRIAL) || subscriptionStatus || cancelStatus}
            className={`zitles-button submit subscription-button trial ${subscriptionStatus || cancelStatus ? 'disable' : ''}`}
            onClick={(): Promise<void> => handleSubscription(SubscriptionPlan.TRIAL)}
            startIcon={(submitLoading && subscriptionPlan === SubscriptionPlan.TRIAL) ? <CircularProgress size={14} color='inherit' /> : null}>
            {
              submitLoading && subscriptionPlan === SubscriptionPlan.TRIAL
                ? 'Subscribing'
                : trialStatus
                  ? 'View Trial'
                  : 'Get Started'
            }
          </Button>
        </TableCell>
        <TableCell className={'table-cell-text'} align={'center'}>
          <Button
            id={'btn-subscription-solo'}
            disabled={(submitLoading && subscriptionPlan === SubscriptionPlan.SOLO) || trialStatus}
            className={`zitles-button submit subscription-button solo ${trialStatus ? 'disable' : ''}`}
            onClick={(): Promise<void> => handleSubscription(SubscriptionPlan.SOLO)}
            startIcon={(submitLoading && subscriptionPlan === SubscriptionPlan.SOLO) ? <CircularProgress size={14} color='inherit' /> : null}>
            {
              submitLoading && subscriptionPlan === SubscriptionPlan.SOLO
                ? 'Subscribing'
                : subscriptionStatus
                  ? 'View Subscription'
                  : 'Get Started'
            }
          </Button>
        </TableCell>
        <TableCell className={'table-cell-text'} align={'center'}>
          <Button
            id={'btn-subscription-enterprise'}
            className={'zitles-button submit subscription-button enterprise'}
            onClick={(): Promise<void> => handleSubscription(SubscriptionPlan.ENTERPRISE)}
            startIcon={(submitLoading && subscriptionPlan === SubscriptionPlan.ENTERPRISE) ? <CircularProgress size={14} color='inherit' /> : null}>
            {
              submitLoading && subscriptionPlan === SubscriptionPlan.ENTERPRISE
                ? 'Subscribing'
                : 'Book a Call'
            }
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <Box className={'body-box'}>
        {
          loading
            ? <LoadingComponent/>
            : <Box>
              <Typography className={'subscription-header-title'}>Choose Your Zitles Plan</Typography>
              <Typography className={'subscription-header-description'}>Simplify Your Title Management</Typography>
              <Paper className={'table-container data-table'} elevation={0}>
                <TableContainer className={'zitles-table'}>
                  <Table>
                    <TableHead>
                      <TableRow className={'table-header'}>
                        {
                          tableHeaders.map((header: string, index: number): JSX.Element => (
                            <TableCell key={header} align='center' padding='normal' className={index === 0 ? 'subscription-feature' : 'subscription-plans'}>
                              <Typography id={`column-subscription-table-${index}`} className={'data-table-header'}>{header}</Typography>
                            </TableCell>
                          ))
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        subscriptionResources.map(renderTableRow)
                      }
                      {
                        renderTableButtons()
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
        }
      </Box>
      {
        openSubscriptionDialog &&
          <SubscriptionDialog
            open={openSubscriptionDialog}
            setOpen={setOpenSubscriptionDialog}
            subscriptions={subscriptions}
            plan={subscriptionPlan}
            status={subscriptionPlan === SubscriptionPlan.TRIAL ? SubscriptionStatus.TRAIL : SubscriptionStatus.ACTIVE}
            setReload={setReload}/>
      }
    </>
  );
}

export default observer(Subscription);
